import { Injectable } from '@angular/core';

/** Storage Type Options */
export type StorageType = 'local' | 'session';

/** Storage Configuration */
export interface StorageConfig {
  type: StorageType;
  prefix?: string;
}

/**
 * Generic Storage Service
 *
 * Scalable service that can work with localStorage, sessionStorage, or other storage implementations. Provides a
 * unified interface for storing and retrieving application state.
 *
 * Usage:
 *
 * ```ts
 * // Inject with default config (localStorage)
 * constructor(private storage: StorageService) {}
 *
 * // Save data
 * this.storage.set('key', { data: 'value' });
 *
 * // Get data
 * const data = this.storage.get('key');
 *
 * // Use sessionStorage instead
 * this.storage.setConfig({ type: 'session' });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private config: StorageConfig = {
    type: 'local',
    prefix: '@app',
  };

  /** Set storage configuration */
  setConfig(config: Partial<StorageConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /** Get current storage configuration */
  getConfig(): StorageConfig {
    return { ...this.config };
  }

  /** Get the appropriate storage backend */
  private getStorage(): Storage {
    return this.config.type === 'session' ? sessionStorage : localStorage;
  }

  /** Generate a prefixed key */
  private getPrefixedKey(key: string): string {
    return this.config.prefix ? `${this.config.prefix}:${key}` : key;
  }

  /**
   * Store a value in the current storage backend
   *
   * @param key The key to store
   * @param value The value to store (will be JSON serialized)
   * @returns True if successful, false otherwise
   */
  set<T>(key: string, value: T): boolean {
    try {
      const storage = this.getStorage();
      const prefixedKey = this.getPrefixedKey(key);
      const serialized = JSON.stringify(value);
      storage.setItem(prefixedKey, serialized);
      return true;
    } catch (error) {
      console.error(`Failed to store key "${key}":`, error);
      return false;
    }
  }

  /**
   * Retrieve a value from the current storage backend
   *
   * @param key The key to retrieve
   * @param defaultValue Default value if key doesn't exist or parsing fails
   * @returns The stored value or default value
   */
  get<T>(key: string, defaultValue?: T): T | null {
    try {
      const storage = this.getStorage();
      const prefixedKey = this.getPrefixedKey(key);
      const item = storage.getItem(prefixedKey);

      if (item === null) {
        return defaultValue ?? null;
      }

      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to retrieve key "${key}":`, error);
      return defaultValue ?? null;
    }
  }

  /**
   * Check if a key exists in the current storage backend
   *
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  has(key: string): boolean {
    try {
      const storage = this.getStorage();
      const prefixedKey = this.getPrefixedKey(key);
      return storage.getItem(prefixedKey) !== null;
    } catch (error) {
      console.error(`Failed to check key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove a key from the current storage backend
   *
   * @param key The key to remove
   * @returns True if successful, false otherwise
   */
  remove(key: string): boolean {
    try {
      const storage = this.getStorage();
      const prefixedKey = this.getPrefixedKey(key);
      storage.removeItem(prefixedKey);
      return true;
    } catch (error) {
      console.error(`Failed to remove key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clear all keys with the current prefix from the storage backend
   *
   * @returns True if successful, false otherwise
   */
  clear(): boolean {
    try {
      const storage = this.getStorage();
      const prefix = this.getPrefixedKey('');
      const keysToRemove: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Failed to clear storage:', error);
      return false;
    }
  }

  /**
   * Get all keys with the current prefix from the storage backend
   *
   * @returns Array of keys
   */
  keys(): string[] {
    try {
      const storage = this.getStorage();
      const prefix = this.getPrefixedKey('');
      const keys: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) {
          // Remove the prefix from the key
          const cleanKey = key.substring(prefix.length);
          keys.push(cleanKey);
        }
      }

      return keys;
    } catch (error) {
      console.error('Failed to get keys:', error);
      return [];
    }
  }

  /**
   * Watch for changes in a specific key (for multi-tab sync)
   *
   * @param key The key to watch
   * @param callback Function to call when the key changes
   * @returns Unsubscribe function
   */
  watch<T>(key: string, callback: (value: T | null) => void): () => void {
    const prefixedKey = this.getPrefixedKey(key);

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === prefixedKey) {
        try {
          const value = event.newValue ? (JSON.parse(event.newValue) as T) : null;
          callback(value);
        } catch (error) {
          console.error(`Failed to parse storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Return unsubscribe function
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }
}
